import React from 'react';
import { Radio, ExternalLink, ShieldCheck } from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

interface PreparednessResource {
  title: string;
  category: string;
  description: string;
  officialGuidance: string;
  resourceLink: string;
  isAffiliate: boolean;
}

export const AffiliateSection: React.FC = () => {
  const affiliatesActive = APP_CONFIG.monetization.affiliates.enabled;

  // Authoritative emergency readiness equipment vetted against ready.gov recommendations
  const resources: PreparednessResource[] = [
    {
      title: 'NOAA Weather Radio (All-Hazards Emergency Receiver)',
      category: 'Emergency Communications',
      description: 'Continuous broadcast of official National Weather Service warnings, watches, and hazard information 24/7 with battery backup.',
      officialGuidance: 'Recommended by FEMA and Ready.gov for power-outage communications.',
      resourceLink: 'https://www.weather.gov/nwr/',
      isAffiliate: false, // Authoritative reference link unless affiliate is enabled
    },
    {
      title: 'Hand-Crank LED Flashlight & Emergency USB Power Bank',
      category: 'Power & Illumination',
      description: 'Self-powered illumination and phone charging during severe electrical grid disruption.',
      officialGuidance: 'Essential item in Ready.gov Basic Disaster Kit.',
      resourceLink: 'https://www.ready.gov/kit',
      isAffiliate: false,
    },
  ];

  return (
    <section aria-labelledby="preparedness-resources-title" className="my-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 id="preparedness-resources-title" className="text-lg font-bold text-navy-950 flex items-center gap-2">
            <Radio className="w-5 h-5 text-blue-600" />
            <span>Emergency Preparedness & Official Equipment</span>
          </h2>
          <p className="text-xs text-slate-600 mt-0.5">
            Standard emergency equipment recommended by federal disaster agencies for storm readiness.
          </p>
        </div>
        {affiliatesActive && (
          <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">
            Affiliate Disclosure Active
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {resources.map((item, idx) => (
          <div
            key={idx}
            className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col justify-between hover:border-slate-300 transition-shadow"
          >
            <div>
              <div className="text-[11px] font-semibold text-blue-700 uppercase tracking-wider mb-1">
                {item.category}
              </div>
              <h3 className="font-semibold text-navy-900 text-sm leading-snug">
                {item.title}
              </h3>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                {item.description}
              </p>
              <div className="flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded mt-2.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span>{item.officialGuidance}</span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">
                Official specifications guide
              </span>
              <a
                href={item.resourceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                <span>Learn more</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-500 mt-3 italic">
        * Note: SignalNear does not sell equipment directly. Informational references follow public guidance from Ready.gov and the National Oceanic and Atmospheric Administration.
      </p>
    </section>
  );
};

